import { html, TemplateResult } from 'lit';
import { LitBaseModal } from '../../../../../webmodule-common/other/ui/modal/modal-factory-lit';
import type { NullPromise } from '../../../../../webmodule-common/other/ui/events';
import type { WebmoduleInput } from '../../../../../webmodule-common/components/src/webmodule-components';
import { fireQuickErrorToast } from '../../../../../webmodule-common/other/toast-away';
import { PurchaseItemType } from '../../../../api/dealer-api-interface-all';
import { pickResourceFamily } from './resource-family-picker';
import { pickFinishColor } from './finish-color-step-picker';
import { pickExtrusionLength } from './extrusion-length-step-picker';
import { AdHocLineSelection, parseObjectReference } from '../../data/ad-hoc-line-input';
import type { FamilyResourceTag } from '../../../../picker/family-resource-picker-data';
import { type FinishColorOption, v6Util } from '../../../../../webmodule-common/interop/interfaces/v6-config';
import { LangFabricationPurchaseOrder } from '../../../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';

// V6 resource-class numbers per material type (used by pickResourceFamily).
const RESOURCE_CLASS_EXTRUSION = 3;
const RESOURCE_CLASS_COMPONENT = 2;
const RESOURCE_CLASS_GASKET = 211;
const RESOURCE_CLASS_FILL = 4;

export interface AdHocLineContext {
  purchaseOrderId: string;
  recordVersion: string;
  supplierId: string;
}

// Map a picked family/resource tag onto the AdHocLineSelection.resource shape.
// code/description come straight off the tag; libraryCode is parsed out of objectReference.
function resourceFromTag(tag: FamilyResourceTag): AdHocLineSelection['resource'] {
  return {
    libraryCode: parseObjectReference(tag.objectReference).libraryCode,
    code: tag.code,
    description: tag.description ?? ''
  };
}

// Map a picked finish color onto the AdHocLineSelection.finish shape (mirrors resourceFromTag).
// code comes off the option rather than the parsed objref: it is V6's own CodeOf output, which is
// the bare code the backend keys on. Only libraryCode has to be parsed - V6 does not supply it.
function finishFromOption(finish: FinishColorOption): NonNullable<AdHocLineSelection['finish']> {
  return {
    libraryCode: parseObjectReference(finish.reference).libraryCode,
    code: finish.code,
    description: finish.description
  };
}

// Parse a typed measurement via V6, surfacing the standard offline / invalid-length toasts on
// failure. Returns the parsed result (internalValue + displayText) or null if it could not be
// parsed - callers bail on null. Shared by the gasket length and the fill width/height.
async function parseMeasurementOrToast(supplierId: string, text: string) {
  const parsed = await v6Util().parseMeasurement(supplierId, text);
  if (!parsed) {
    fireQuickErrorToast(LangFabricationPurchaseOrder.lengthCheckOffline());
    return null;
  }
  // A zero/negative measurement parses as "valid" but is not a usable length: it would create a
  // degenerate line (gasket quantity 0, or a 0-dimension fill), so reject it alongside invalid
  // text - the user gets a clear message instead of the backend's generic non-positive error.
  if (!parsed.valid || parsed.internalValue <= 0) {
    fireQuickErrorToast(LangFabricationPurchaseOrder.invalidLengthError());
    return null;
  }
  return parsed;
}

export async function addExtrusionLine(ctx: AdHocLineContext): NullPromise<AdHocLineSelection> {
  const tag = await pickResourceFamily(
    ctx.supplierId,
    RESOURCE_CLASS_EXTRUSION,
    LangFabricationPurchaseOrder.pickExtrusionTitle()
  );
  if (!tag) return null;

  const finish = await pickFinishColor(
    ctx.supplierId,
    'extrusion',
    tag.objectReference,
    LangFabricationPurchaseOrder.pickFinishTitle()
  );
  if (!finish) return null;

  const length = await pickExtrusionLength(
    ctx.supplierId,
    tag.objectReference,
    finish.reference,
    LangFabricationPurchaseOrder.pickLengthTitle()
  );
  if (!length) return null;

  return {
    purchaseOrderId: ctx.purchaseOrderId,
    recordVersion: ctx.recordVersion,
    itemType: PurchaseItemType.Extrusion,
    resource: resourceFromTag(tag),
    finish: finishFromOption(finish),
    notOrder: false,
    quantity: 1,
    barLength: length.length,
    barLengthDisplay: length.display
  };
}

export async function addComponentLine(ctx: AdHocLineContext): NullPromise<AdHocLineSelection> {
  const tag = await pickResourceFamily(
    ctx.supplierId,
    RESOURCE_CLASS_COMPONENT,
    LangFabricationPurchaseOrder.pickComponentTitle()
  );
  if (!tag) return null;

  const finish = await pickFinishColor(
    ctx.supplierId,
    'component',
    tag.objectReference,
    LangFabricationPurchaseOrder.pickFinishTitle()
  );
  if (!finish) return null;

  return {
    purchaseOrderId: ctx.purchaseOrderId,
    recordVersion: ctx.recordVersion,
    itemType: PurchaseItemType.Component,
    resource: resourceFromTag(tag),
    finish: finishFromOption(finish),
    notOrder: false,
    quantity: 1
  };
}

export async function addGasketLine(ctx: AdHocLineContext): NullPromise<AdHocLineSelection> {
  const tag = await pickResourceFamily(
    ctx.supplierId,
    RESOURCE_CLASS_GASKET,
    LangFabricationPurchaseOrder.pickGasketTitle()
  );
  if (!tag) return null;

  const finish = await pickFinishColor(
    ctx.supplierId,
    'gasket',
    tag.objectReference,
    LangFabricationPurchaseOrder.pickFinishTitle()
  );
  if (!finish) return null;

  // A gasket's quantity is a length (measurement), so prompt for it (like the fill size dialog)
  // and submit the V6-parsed internal value as the quantity; the backend derives the display
  // length, mirroring the items-table length input. Default the field to one roll length (the
  // finish carries the gasket's roll length back from get-finishes-by-resource).
  const entry = await promptMeasurements({
    title: LangFabricationPurchaseOrder.gasketLengthTitle(),
    fields: [
      { key: 'length', label: LangFabricationPurchaseOrder.gasketLengthLabel(), value: finish.rollLengthDisplay }
    ]
  });
  if (!entry) return null;

  const parsed = await parseMeasurementOrToast(ctx.supplierId, entry.length);
  if (!parsed) return null;

  return {
    purchaseOrderId: ctx.purchaseOrderId,
    recordVersion: ctx.recordVersion,
    itemType: PurchaseItemType.Gasket,
    resource: resourceFromTag(tag),
    finish: finishFromOption(finish),
    notOrder: false,
    quantity: parsed.internalValue,
    // Carry the roll length onto the line (only when it has one) so the items-table "(N rolls)"
    // hint shows for it, like BOM-sourced gaskets.
    ...(finish.rollLength > 0 ? { rollLength: finish.rollLength, rollLengthDisplay: finish.rollLengthDisplay } : {})
  };
}

export async function addFillLine(ctx: AdHocLineContext): NullPromise<AdHocLineSelection> {
  const tag = await pickResourceFamily(
    ctx.supplierId,
    RESOURCE_CLASS_FILL,
    LangFabricationPurchaseOrder.pickGlassTitle()
  );
  if (!tag) return null;

  const entry = await promptMeasurements({
    title: LangFabricationPurchaseOrder.fillSizeTitle(),
    fields: [
      { key: 'width', label: LangFabricationPurchaseOrder.fillWidthLabel() },
      { key: 'height', label: LangFabricationPurchaseOrder.fillHeightLabel() }
    ]
  });
  if (!entry) return null;

  const parsedW = await parseMeasurementOrToast(ctx.supplierId, entry.width);
  if (!parsedW) return null;
  const parsedH = await parseMeasurementOrToast(ctx.supplierId, entry.height);
  if (!parsedH) return null;

  return {
    purchaseOrderId: ctx.purchaseOrderId,
    recordVersion: ctx.recordVersion,
    itemType: PurchaseItemType.Fill,
    resource: resourceFromTag(tag),
    finish: null,
    notOrder: false,
    quantity: 1,
    width: parsedW.internalValue,
    widthDisplay: parsedW.displayText,
    height: parsedH.internalValue,
    heightDisplay: parsedH.displayText
  };
}

interface MeasurementField {
  key: string;
  label: string;
  // Optional pre-filled value (e.g. a gasket's roll length as the default order length).
  value?: string;
}

interface MeasurementDialogOptions {
  title: string;
  fields: MeasurementField[];
}

// Minimal measurement-entry dialog. Extends LitBaseModal (the base ModalPickerDialog also uses)
// with one webmodule-input text field per requested measurement + OK/Cancel. Returns the raw
// typed text keyed by field; the caller parses each via parseMeasurementOrToast, mirroring the
// items-table length input. Used for the fill width x height and the gasket length.
class MeasurementEntryDialog extends LitBaseModal {
  private options: MeasurementDialogOptions;
  private resolveResult: (value: Record<string, string> | null) => void;
  private values: Record<string, string> = {};

  constructor(options: MeasurementDialogOptions, resolveResult: (value: Record<string, string> | null) => void) {
    super();
    this.options = options;
    this.resolveResult = resolveResult;
    // Seed with any pre-filled defaults so an unchanged field still returns its default value.
    this.values = Object.fromEntries(options.fields.map(field => [field.key, field.value ?? '']));
  }

  protected get modalSize() {
    return 'modal-sm';
  }

  title() {
    return this.options.title;
  }

  bodyTemplate(): TemplateResult {
    return html`
      <div class="d-flex flex-column gap-3 py-2">
        ${this.options.fields.map(
          field =>
            html`<webmodule-input
              size="small"
              type="text"
              label=${field.label}
              value=${field.value ?? ''}
              @webmodule-change=${(e: Event) => (this.values[field.key] = (e.target as WebmoduleInput).value)}
            ></webmodule-input>`
        )}
      </div>
    `;
  }

  footerTemplate(): TemplateResult {
    return this.createConfirmCancelButtons(
      LangFabricationPurchaseOrder.measurementOk(),
      async () => {
        this.resolveResult(
          Object.fromEntries(this.options.fields.map(field => [field.key, (this.values[field.key] ?? '').trim()]))
        );
        await this.hideModal();
      },
      LangFabricationPurchaseOrder.measurementCancel(),
      async () => {
        this.resolveResult(null);
        await this.hideModal();
      }
    );
  }
}

function promptMeasurements(options: MeasurementDialogOptions): NullPromise<Record<string, string>> {
  return new Promise<Record<string, string> | null>(resolve => {
    const dialog = new MeasurementEntryDialog(options, resolve);
    // showModal resolves when the modal closes; if it closed without a footer choice
    // (e.g. backdrop dismiss) treat it as a cancel.
    dialog.showModal().then(() => resolve(null));
  });
}
